/**
 * Relationship Cache Manager for VA Sprouts
 * Manages caching of job applications, contracts, and interviews
 * Independent cache management without dependencies on messaging services
 */

class RelationshipCacheManager {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Generate a cache key for different types of data
   */
  generateKey(type, id, suffix = '') {
    return `${type}:${id}${suffix ? ':' + suffix : ''}`;
  }

  /**
   * Set cache with expiry
   */
  set(key, value, ttl = this.defaultTTL) {
    this.cache.set(key, value);
    this.cacheExpiry.set(key, Date.now() + ttl);
  }

  /**
   * Get cache value if not expired
   */
  get(key) {
    const expiry = this.cacheExpiry.get(key);
    if (expiry && Date.now() > expiry) {
      this.cache.delete(key);
      this.cacheExpiry.delete(key);
      return null;
    }
    return this.cache.get(key) || null;
  }

  /**
   * Delete cache entry
   */
  delete(key) {
    this.cache.delete(key);
    this.cacheExpiry.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  /**
   * Invalidate cache for job application
   */
  invalidateCacheForJobApplication(applicationId) {
    const patterns = [
      `application:${applicationId}`,
      `job_applications:`,
      `employer_applications:`,
      `va_applications:`
    ];

    for (const [key] of this.cache) {
      if (patterns.some(pattern => key.includes(pattern))) {
        this.delete(key);
      }
    }
  }

  /**
   * Invalidate cache for job-related data
   */
  invalidateCacheForJob(jobId) {
    const patterns = [
      `job:${jobId}`,
      `job_applications:job:${jobId}`,
      `job_stats:${jobId}`
    ];

    for (const [key] of this.cache) {
      if (patterns.some(pattern => key.includes(pattern))) {
        this.delete(key);
      }
    }
  }

  /**
   * Invalidate cache for user-related data
   */
  invalidateCacheForUser(userId) {
    const patterns = [
      `user:${userId}`,
      `va_applications:${userId}`,
      `employer_applications:${userId}`,
      `user_profile:${userId}`
    ];

    for (const [key] of this.cache) {
      if (patterns.some(pattern => key.includes(pattern))) {
        this.delete(key);
      }
    }
  }

  /**
   * Handle job application events
   */
  async onJobApplication(vaId, jobId) {
    // Invalidate related caches
    this.invalidateCacheForUser(vaId);
    this.invalidateCacheForJob(jobId);
    
    // Invalidate conversation caches
    const patterns = [`conversations:${vaId}`, `conversation_participants:`];
    for (const [key] of this.cache) {
      if (patterns.some(pattern => key.includes(pattern))) {
        this.delete(key);
      }
    }
  }

  /**
   * Handle job application status changes
   */
  async onJobApplicationStatusChange(applicationId, newStatus, vaId, employerId) {
    this.invalidateCacheForJobApplication(applicationId);
    this.invalidateCacheForUser(vaId);
    this.invalidateCacheForUser(employerId);
    
    // Clear relevant conversation caches
    this.delete(`conversations:${vaId}`);
    this.delete(`conversations:${employerId}`);
  }

  /**
   * Handle contract creation events
   */
  onContractCreated(vaId, employerId, contractData = {}) {
    // Invalidate related caches
    if (contractData.job_id) {
      this.invalidateCacheForJob(contractData.job_id);
    }
    this.invalidateCacheForUser(vaId);
    this.invalidateCacheForUser(employerId);
    
    // Clear conversation caches
    this.delete(`conversations:${vaId}`);
    this.delete(`conversations:${employerId}`);
  }

  /**
   * Handle contract updates
   */
  onContractUpdated(contractId, vaId, employerId) {
    this.invalidateCacheForUser(vaId);
    this.invalidateCacheForUser(employerId);
    this.delete(`contract:${contractId}`);
  }

  /**
   * Handle interview creation events
   */
  onInterviewCreated(interviewData) {
    // Invalidate related caches
    if (interviewData.job_id) {
      this.invalidateCacheForJob(interviewData.job_id);
    }
    if (interviewData.va_id) {
      this.invalidateCacheForUser(interviewData.va_id);
    }
    if (interviewData.employer_id) {
      this.invalidateCacheForUser(interviewData.employer_id);
    }
  }

  /**
   * Get cache statistics (alias for getStats for compatibility)
   */
  getCacheStats() {
    return this.getStats();
  }

  /**
   * Check if realtime system is healthy
   */
  isRealtimeHealthy() {
    // Simple health check based on cache performance
    const stats = this.getStats();
    return stats.totalEntries < 10000 && stats.expiredEntries < stats.totalEntries * 0.1;
  }

  /**
   * Clear all cache entries (alias for clear for compatibility)
   */
  clearCache() {
    this.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      totalEntries: this.cache.size,
      expiredEntries: Array.from(this.cacheExpiry.entries())
        .filter(([, expiry]) => Date.now() > expiry).length,
      memoryUsage: JSON.stringify(Array.from(this.cache.entries())).length
    };
  }

  /**
   * Clean up expired entries
   */
  cleanup() {
    const now = Date.now();
    const expiredKeys = [];

    for (const [key, expiry] of this.cacheExpiry) {
      if (now > expiry) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach(key => {
      this.cache.delete(key);
      this.cacheExpiry.delete(key);
    });

    return expiredKeys.length;
  }
}

// Create and export singleton instance
const relationshipCacheManager = new RelationshipCacheManager();

export default relationshipCacheManager;
export { RelationshipCacheManager };
